import React, { useState, useEffect } from 'react';
import './UpdateNotification.css';

const UpdateNotification = () => {
  const [updateInfo, setUpdateInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showScheduler, setShowScheduler] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('02:00');
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    checkForUpdates();
    // Check every 30 seconds
    const interval = setInterval(checkForUpdates, 30 * 1000);
    return () => clearInterval(interval);
  }, []);

  const checkForUpdates = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/update-info`);
      const data = await response.json();
      console.log('Update Info Response:', data);
      setUpdateInfo(data);
      
      // If update is available and not dismissed, show notification
      if (data.updateAvailable && dismissed) {
        setDismissed(false);
      }
    } catch (error) {
      console.error('Failed to check for updates:', error);
    }
  };

  const handleUpdateNow = async () => {
    if (!window.confirm('This will restart the service and reload the page. Continue?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/update-version`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'now' })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Update failed');
      }

      const data = await response.json();
      
      // Show countdown and reload
      let countdown = 8;
      const countdownInterval = setInterval(() => {
        countdown--;
        if (countdown <= 0) {
          clearInterval(countdownInterval);
          window.location.reload();
        }
      }, 1000);
      
    } catch (error) {
      console.error('Update failed:', error);
      alert('Update failed: ' + error.message);
      setLoading(false);
    }
  };

  const handleScheduleUpdate = async () => {
    if (!selectedDate) {
      alert('Please select a date and time');
      return;
    }

    const scheduledDateTime = new Date(`${selectedDate}T${selectedTime}`);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/update-version`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          type: 'schedule', 
          time: scheduledDateTime.toISOString() 
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to schedule update');
      }

      const data = await response.json();
      alert(data.message);
      setShowScheduler(false);
      checkForUpdates();
    } catch (error) {
      console.error('Failed to schedule update:', error);
      alert('Failed to schedule update: ' + error.message);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
  };

  // Debug logging
  console.log('UpdateNotification render check:', {
    updateInfo,
    dismissed,
    shouldShow: updateInfo && updateInfo.updateAvailable && !(dismissed && updateInfo.daysRemaining > 0)
  });

  // Don't show notification if no update available or dismissed (unless critical)
  if (!updateInfo || !updateInfo.updateAvailable || (dismissed && updateInfo.daysRemaining > 0)) {
    return null;
  }

  const isCritical = updateInfo.daysRemaining <= 7;
  const isUrgent = updateInfo.daysRemaining === 0;

  return (
    <>
      <div className={`update-notification ${isCritical ? 'critical' : ''} ${isUrgent ? 'urgent' : ''}`}>
        <div className="update-header">
          <span className="update-icon">🔔</span>
          <strong>New Version Available!</strong>
          <span className="update-versions">
            {updateInfo.latestVersion} (Current: {updateInfo.currentVersion})
          </span>
        </div>
        
        {updateInfo.daysRemaining !== null && (
          <div className="update-countdown">
            {updateInfo.daysRemaining > 0 ? (
              <>
                <span className="countdown-icon">⏰</span>
                Auto-update in <strong>{updateInfo.daysRemaining}</strong> day{updateInfo.daysRemaining !== 1 ? 's' : ''}
              </>
            ) : (
              <>
                <span className="countdown-icon">⚠️</span>
                <strong>Auto-update will occur soon!</strong>
              </>
            )}
          </div>
        )}

        <div className="update-actions">
          <button
            onClick={handleUpdateNow}
            disabled={loading}
            className="btn-update-now"
          >
            {loading ? 'Updating...' : 'Update Now'}
          </button>
          
          {!updateInfo.isScheduled && updateInfo.daysRemaining > 0 && (
            <button
              onClick={() => setShowScheduler(!showScheduler)}
              className="btn-schedule"
              disabled={loading}
            >
              Schedule Update
            </button>
          )}
          
          {updateInfo.isScheduled && (
            <span className="scheduled-info">
              Scheduled for: {new Date(updateInfo.scheduledTime).toLocaleString()}
            </span>
          )}
          
          {!isCritical && (
            <button
              onClick={handleDismiss}
              className="btn-dismiss"
              disabled={loading}
            >
              ✕
            </button>
          )}
        </div>

        {showScheduler && (
          <div className="scheduler">
            <div className="scheduler-inputs">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                max={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
              />
              <input
                type="time"
                value={selectedTime}
                onChange={(e) => setSelectedTime(e.target.value)}
              />
              <button onClick={handleScheduleUpdate} className="btn-schedule-confirm">
                Confirm Schedule
              </button>
              <button onClick={() => setShowScheduler(false)} className="btn-cancel">
                Cancel
              </button>
            </div>
            <p className="scheduler-note">
              Updates can be scheduled up to 30 days in advance
            </p>
          </div>
        )}
      </div>

      {loading && (
        <div className="update-loader-overlay">
          <div className="update-loader-content">
            <div className="loader-spinner"></div>
            <div className="loader-text">Updating RBAC Service...</div>
            <div className="progress-steps">
              <div className="progress-step active">📦 Downloading update package...</div>
              <div className="progress-step">📂 Extracting files...</div>
              <div className="progress-step">🔄 Updating components...</div>
              <div className="progress-step">🚀 Restarting service...</div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UpdateNotification;
